/* istanbul ignore file */
import { styled } from '@mui/material/styles';

import {
    Theme,
    containerLargeWidth,
    rem,
    remToGapMultiplier,
} from '../../theme';
import { ButtonBase } from '../Button';
import { Heading } from '../Heading';
import { Icon } from '../Icon';

import { GymHeaderVariant } from './GymHeader.types';

export const StyledContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing([rem(1.5), rem(3)], [3.75, 7.5])};
        color: ${theme.palette.common.blue};
        background-color: ${theme.palette.grey[100]};
	`}
`;

export const StyledContainerInner = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        ${theme.breakpoints.up('desktop')} {
            flex-direction: row;
            max-width: ${theme.tggSpacing(rem(containerLargeWidth), containerLargeWidth * remToGapMultiplier)};
            margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};
        }
	`}
`;

export const StyledHeading = styled(Heading)`
    ${({ theme }: { theme: Theme }) => `
        margin: ${theme.tggSpacing([0, 0, rem(1)], [0, 0, 2.5])};
	`}

    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
        ${({ theme }: { theme: Theme }) => `
            margin-bottom: ${theme.tggSpacing(rem(2.2), 9)};
        `}
        ${({ theme }: { theme: Theme }) => theme.typography.text_32_bold};
    }
`;

export const StyledTrustpilotContainer = styled('div')`
    display: flex;
    align-items: center;
`;

export const StyledStarsImage = styled('img')`
    ${({ theme }: { theme: Theme }) => `
        height: ${theme.tggSpacing(rem(2), 5)};

        ${theme.breakpoints.up('desktop')} {
            height: ${theme.tggSpacing(rem(2.5), 6.25)};
        }
	`}
`;

export const StyledTrustPilotLink = styled('a')<{
    $decorate?: boolean;
    theme: Theme;
}>`
    display: inline-flex;
    color: ${({ theme }: { theme: Theme }) => theme.palette.common.blue};
    text-decoration: ${({ $decorate }) => ($decorate ? 'underline' : 'none')};

    &:hover {
        text-decoration: none;
    }
`;

export const StyledCtaLink = styled('a')`
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }
`;

export const StyledStarDescription = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        margin-left: ${theme.tggSpacing(rem(1), 2.5)};
	`}
`;

export const StyledJoinContainer = styled('div')<{
    $variant: GymHeaderVariant;
    theme: Theme;
}>`
    ${({ theme, $variant }) => `
        display: flex;
        flex-shrink: 0;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: ${theme.tggSpacing([rem(1), rem(-3), rem(-1.5)], [2.5, -7.5, -3.75])};
        color: ${theme.palette.common.blue};
        background-color: ${theme.palette.common.white};

        ${theme.breakpoints.up('desktop')} {
            justify-content: ${
                $variant === 'join' ? 'center' : 'space-between'
            };
            width: ${$variant === 'join' ? theme.tggSpacing(rem(19), 47.5) : theme.tggSpacing(rem(23.5), 58.75)};
            height: ${theme.tggSpacing(rem(17.1), 42.75)};
            margin: ${theme.tggSpacing([0, 0, 0, rem(3)], [0, 0, 0, 7.5])};
            border-radius: ${theme.tggSpacing(rem(1.6), 4)};
        }
	`}
`;

export const StyledIcon = styled(Icon)`
    ${({ theme }: { theme: Theme }) => `
        min-width: ${theme.tggSpacing(rem(1.5), 3.75)};
        margin-top: ${theme.tggSpacing(rem(0.3), 1)};

        ${theme.breakpoints.up('desktop')} {
            min-width: ${theme.tggSpacing(rem(2.2), 9)};
            margin-top: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledOpeningHoursContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        align-items: flex-start;
        margin-top: ${theme.tggSpacing(rem(1), 2.5)};

        ${theme.breakpoints.up('desktop')} {
            margin-top: ${theme.tggSpacing(rem(1), 2.5)};
        }
	`}
`;

export const StyledOpeningHoursText = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        margin-left: ${theme.tggSpacing(rem(1), 2.5)};
	`}
`;

export const StyledOpeningHoursLink = styled('a')`
    color: ${({ theme }: { theme: Theme }) => theme.palette.common.blue};
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }

    svg {
        cursor: pointer;
    }
`;

export const StyledOpeningHoursIcon = styled(Icon)`
    ${({ theme }: { theme: Theme }) => `
        margin-bottom: ${theme.tggSpacing(rem(0.5), 1.25)};
        margin-left: ${theme.tggSpacing(rem(0.5), 1.25)};
	`}
`;

export const StyledAddressContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        align-items: flex-start;
        margin-top: ${theme.tggSpacing(rem(1), 2.5)};
        margin-bottom: ${theme.tggSpacing(rem(1.5), 3.75)};
	`}
`;

export const StyledAddressLink = styled('a')`
    ${({ theme }: { theme: Theme }) => `
        margin-left: ${theme.tggSpacing(rem(1), 2.5)};
        color: ${theme.palette.common.blue};
        text-decoration: underline;

        &:hover {
            color: ${theme.palette.common.blue};
            text-decoration: none;
        }
	`}
`;

export const StyledJoinDescription = styled('div')<{
    $variant: GymHeaderVariant;
    theme: Theme;
}>`
    ${({ theme, $variant }) => `
        margin-top: ${theme.tggSpacing(rem(1.5), 3.75)};

        ${theme.breakpoints.up('desktop')} {
            margin-top: ${$variant === 'join' ? theme.tggSpacing(0, 0) : theme.tggSpacing(rem(1.5), 3.75)};
            text-align: center;
        }
	`}
`;

export const StyledPrice = styled(Heading)`
    ${({ theme }: { theme: Theme }) => `
        && {
            margin: ${theme.tggSpacing(0, 0)};
        }

        ${theme.breakpoints.up('desktop')} {
            display: block;
        }
	`}
`;

export const StyledRegisterText = styled('div')`
    ${({ theme }: { theme: Theme }) =>
        theme.typography.text_16_lh_15_desktop_lh_13};
    ${({ theme }: { theme: Theme }) => `
        ${theme.breakpoints.up('desktop')} {
            display: block;
            width: ${theme.tggSpacing(rem(19.5), 48.75)};
            padding: ${theme.tggSpacing([rem(1), rem(3), 0], [2.5, 7.5, 0])};
            margin-right: ${theme.tggSpacing(rem(2), 5)};
            margin-left: ${theme.tggSpacing(rem(2), 5)};
        }
	`}
`;

export const StyledJoinNowButton = styled(ButtonBase)`
    ${({ theme }: { theme: Theme }) => `
        display: block;
        align-self: stretch;
        margin: ${theme.tggSpacing([rem(1), rem(3), rem(2)], [2.5, 7.5, 5])};
        text-align: center;

        ${theme.breakpoints.up('desktop')} {
            align-self: unset;
            justify-self: flex-end;
            margin: ${theme.tggSpacing([rem(1), 0, rem(2)], [2.5, 0, 5])};
        }
	`}
`;
